import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { AuthShell } from "@/pages/Login";
import { apiResetPassword } from "@/lib/falApi";

function fmtErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      await apiResetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid link" subtitle="This reset link is missing or malformed">
        <Link to="/forgot-password" className="text-sm text-[#faff69] hover:underline" data-testid="request-new-link">Request a new reset link</Link>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Password updated" subtitle="Redirecting you to log in…">
        <div className="rounded-lg border border-zinc-800 bg-[#121212] p-5 text-center" data-testid="reset-done">
          <CheckCircle2 className="h-8 w-8 text-[#34C759] mx-auto" />
          <p className="text-sm text-zinc-300 mt-3">Your password has been changed. You can now log in with your new password.</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password" subtitle="Enter and confirm your new password">
      <form onSubmit={submit} className="space-y-3">
        <Input data-testid="reset-password" type="password" required placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="bg-[#121212] border-zinc-800 focus-visible:ring-[#faff69]" />
        <Input data-testid="reset-confirm" type="password" required placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          className="bg-[#121212] border-zinc-800 focus-visible:ring-[#faff69]" />
        <Button type="submit" disabled={busy} data-testid="reset-submit"
          className="w-full bg-[#faff69] text-black hover:bg-[#e6eb52] font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
        </Button>
      </form>
      <p className="text-sm text-zinc-400 mt-5"><Link to="/login" className="text-[#faff69] hover:underline" data-testid="to-login">Back to log in</Link></p>
    </AuthShell>
  );
}
